<template>
  <div class="flex w-full grow flex-col gap-4 overflow-y-auto p-4">
    <div class="flex justify-between gap-2">
      <div class="flex flex-1 gap-4">
        <div class="flex items-center">
          <img class="size-10" :src="selectedM511Hazard?.icon" alt="" />
        </div>
        <div class="flex flex-1 flex-col">
          <div class="text-wrap text-xl font-medium text-type-primary">
            {{ selectedM511Hazard?.title }}
          </div>
          <div class="text-base text-type-secondary">
            {{ selectedM511Hazard?.description }}
          </div>
          <div class="text-xs font-medium text-type-secondary">
            <ui-relative-time :time="selectedM511RoadClosure?.['started-at'] ?? selectedM511Incident?.publishedAt" />
          </div>
        </div>
      </div>

      <button
        v-if="!hideCloseButton"
        class="flex size-6 min-h-6 min-w-6 cursor-pointer items-center justify-center"
        @click="mapStore.closePopup()"
      >
        <close-rounded aria-label="Close" class="*:fill-type-primary" />
      </button>
    </div>

    <div class="h-px w-full bg-grey-35"></div>

    <ul class="ml-2 grow list-inside list-disc text-base text-type-secondary">
      <li v-if="startedAtFormatted" class="font-semibold">
        <i18n-t keypath="2kP8mNxQ7vR9sL4tE6wA1" :i18n="composer">
          <template #start-time>
            <span class="font-normal">{{ startedAtFormatted }}</span>
          </template>
        </i18n-t>
      </li>
      <li v-if="lastUpdatedAt" class="font-semibold">
        <i18n-t keypath="SgFZC8ud_hQUv2C6aoq0Z" :i18n="composer">
          <template #last-updated>
            <span class="font-normal">{{ lastUpdatedAt }}</span>
          </template>
        </i18n-t>
      </li>
    </ul>
    <div v-if="incidentDescription" class="text-[16px] text-type-secondary" v-html="incidentDescription"></div>
    <p v-if="selectedM511Incident?.['additional-details']" class="text-[16px] text-type-secondary">
      {{ selectedM511Incident?.['additional-details'] }}
    </p>

    <div
      v-if="selectedM511RoadClosure?.['description-note']"
      class="text-[16px] text-type-secondary"
      v-html="selectedM511RoadClosure?.['description-note']"
    ></div>
    <p v-if="selectedM511RoadClosure?.['conditions-and-advice-note']" class="text-[16px] text-type-secondary">
      {{ selectedM511RoadClosure?.['conditions-and-advice-note'] }}
    </p>

    <ui-map-popup-footer
      v-if="showFooter"
      :left-text="t('lSeWusNweldd_c1g_1aJb')"
      :left-callback="zoomToFeatures"
      :right-text="t('_YuYh6TojvigalDAoNPgz')"
      :right-callback="navigateToFAQ"
    />
  </div>
</template>

<script setup lang="ts">
import CloseRounded from '@/common/assets/icons/close-rounded.svg';
import { ClosureEntity, IncidentEntity, useHazardsStore, useMapStore } from '@/features/hazards';
import { formatDateTimeWithYear } from '@/features/hazards/presentation/helpers/format-reported-at-datetime';
import { i18n } from '@/locales';
import { computed, watch } from 'vue';
import { Composer, I18nT } from 'vue-i18n';

withDefaults(
  defineProps<{
    showFooter?: boolean;
    hideCloseButton?: boolean;
  }>(),
  {
    showFooter: true,
    hideCloseButton: false,
  }
);

const { locale, t } = i18n.global;
const mapStore = useMapStore();
const hazardsStore = useHazardsStore();

const selectedM511Hazard = computed(() => selectedM511Incident.value ?? selectedM511RoadClosure.value);

const selectedM511Incident = computed(() => {
  return hazardsStore.hazards?.incidents.find((incident: IncidentEntity) =>
    [mapStore.selectedPopupHazard?.hazardId, hazardsStore.selectedHazard?.hazardId].includes(incident.id)
  );
});

const selectedM511RoadClosure = computed(() => {
  return hazardsStore.hazards?.closures.find((closure: ClosureEntity) =>
    [mapStore.selectedPopupHazard?.hazardId, hazardsStore.selectedHazard?.hazardId].includes(closure.id)
  );
});

const incidentDescription = computed(() => selectedM511Incident.value?.['m511-description']?.replace('No Data', '') ?? '');

const composer = i18n.global as Composer;

const startedAtFormatted = computed(() => {
  // Track locale changes
  void locale.value;

  const startedAt = selectedM511RoadClosure.value?.['started-at'] ?? selectedM511Incident.value?.reportedAt;
  if (!startedAt) return '';

  return formatDateTimeWithYear(startedAt);
});

const lastUpdatedAt = computed(() => {
  void locale.value;

  const lastUpdatedAt = selectedM511RoadClosure.value?.['last-updated-at'] ?? selectedM511Incident.value?.updatedAt;
  if (!lastUpdatedAt) return '';

  return formatDateTimeWithYear(lastUpdatedAt);
});

watch(selectedM511Hazard, (incomingHazard, previousHazard) => {
  // This check ensures that we only close the popup when the hazard is removed,
  // not when it is changed to a different hazard (which happens when a user opens one popup, then another).
  const selectedHazardId = mapStore.selectedPopupHazard?.hazardId || hazardsStore.selectedHazard?.hazardId;

  if (incomingHazard || !previousHazard || selectedHazardId !== previousHazard?.id) return;

  mapStore.closePopup();
});

const zoomToFeatures = () => mapStore.flyToFeatures(selectedM511Hazard.value?.pointFeatures);
const navigateToFAQ = () => window.open('https://www.manitoba511.ca/about/faq');
</script>
